'use client';
/**
 * withStoreContext.ts
 *
 * Utility that sets the PostgreSQL session variable `app.current_store_id`
 * before any store-scoped query. This is the mechanism that makes RLS work
 * correctly during super-admin impersonation:
 *
 *   - The super-admin is the authenticated Supabase user (JWT).
 *   - auth.email() / auth.uid() always resolve to the super-admin.
 *   - The impersonated store_id is tracked in the application layer
 *     (ImpersonationContext → useStoreId → resolveStoreId).
 *   - By calling set_current_store_id(storeId) via RPC, we inject the
 *     correct store_id into the PostgreSQL session so ef_current_store_id()
 *     reads it instead of falling back to the JWT email lookup.
 *
 * Usage:
 *   const supabase = createClient();
 *   const storeId = await getCurrentStoreId();
 *   await setStoreContext(supabase, storeId);
 *   // Now run your query — RLS will use storeId
 */

import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Sets the PostgreSQL session variable `app.current_store_id` for the
 * current connection. Must be called before any store-scoped query when
 * the user is authenticated via Supabase Auth (i.e. not a sub-user).
 *
 * Safe to call with null — the RPC is a no-op if storeId is null.
 */
export async function setStoreContext(
  supabase: SupabaseClient,
  storeId: string | null
): Promise<void> {
  if (!storeId) return;

  try {
    const { error } = await supabase.rpc('set_current_store_id', {
      p_store_id: storeId,
    });
    if (error) {
      // Non-fatal: RLS fallback will still try ef_app_users lookup
      console.warn('[setStoreContext] RPC error:', error.message);
    }
  } catch (err: any) {
    console.warn('[setStoreContext] RPC exception:', err?.message);
  }
}
